import React, { PropsWithChildren } from "react";

import { ComponentBackgroundColorWithDestination } from "types/componentBackground";
import { SectionContainer } from "@silversea-ssc/ui-components-react";
import clsx from "clsx";
import * as styles from "./FindYourCruise.module.scss";
import AlgoliaFYC, { AlgoliaFYCProps } from "./AlgoliaFYC";
import LastSeenProducts from "components/LastSeenProducts/LastSeenProducts";
import { useLastSeenProducts } from "components/LastSeenProducts/useLastSeenProducts";
import { ABTestDomAttr } from "src/ab-test-experiments/client/use-experiment-tag";
import { toPromoAlgoliaCodeMapping } from "./Components/Filters/promo-filters";

type Props = Omit<
  AlgoliaFYCProps,
  "cruiseCardVariant" | "routing" | "promoAlgoliaCodeMapping"
> & {
  showTitle?: boolean;
  showLastSeenProducts?: boolean;
  title?: string | JSX.Element;
  titleAlign?: "left" | "center";
  backgroundColor: ComponentBackgroundColorWithDestination | "neutral10";
  id?: string;
  lastSeenProductSpacingTop?: "compact";
  lastSeenProductsFilter?: {
    destinationWebCode?: string;
  };
} & Partial<ABTestDomAttr>;

const FindYourCruise: React.FC<PropsWithChildren<Props>> = ({
  children,
  backgroundColor,
  showTitle = true,
  title = undefined,
  titleAlign = "center",
  showLastSeenProducts = false,
  lastSeenProductSpacingTop,
  id,
  "abtest-experiment": abtestExperiment,
  "abtest-segment": abtestSegment,
  lastSeenProductsFilter = {},
  ...filtersProps
}) => {
  const { hasLastSeenProducts, ...lastSeenProducts } = useLastSeenProducts({
    ...lastSeenProductsFilter,
    enabled: showLastSeenProducts
  });

  const promoAlgoliaCodeMapping = toPromoAlgoliaCodeMapping(
    filtersProps.promosContent
  );

  return (
    <div
      className={clsx(styles.container, {
        [styles.containerLastSeenPadding]: showLastSeenProducts
      })}
      data-content-name="find_your_cruise"
      id={id || "fyc"}
    >
      {showLastSeenProducts && hasLastSeenProducts && (
        <SectionContainer
          backgroundColor="transparent"
          spacingTop={lastSeenProductSpacingTop}
        >
          <LastSeenProducts {...lastSeenProducts} />
        </SectionContainer>
      )}

      {showTitle && title && (
        <SectionContainer
          backgroundColor="transparent"
          className={clsx({
            [styles.titleSectionLastSeenMargin]: showLastSeenProducts
          })}
        >
          <h2
            className={clsx(styles.title, {
              [styles.titleCenter]: titleAlign == "center",
              [styles.titleLeft]: titleAlign == "left"
            })}
          >
            {title}
          </h2>
        </SectionContainer>
      )}
      <div suppressHydrationWarning>
        <AlgoliaFYC
          {...filtersProps}
          promoAlgoliaCodeMapping={promoAlgoliaCodeMapping}
          backgroundColor={backgroundColor}
        >
          {children}
        </AlgoliaFYC>
      </div>
    </div>
  );
};

export default FindYourCruise;
